import React, { useCallback, useEffect, useId } from 'react';
import { useIntl } from 'react-intl';
import { KitFontId, ReleaseThemeId } from 'fym-shared/src/music24/releaseThemes';
import { TypedMemo } from '../../../../common/types';
import { CatalogItem, SerializedKit, SerializedKitAsset } from '../../../../../redux/api/fymAPI.types';
import { SMLButton } from '../../../components/primitives/SMLButton';
import { useKitWebFonts } from '../../themes/kitWebFonts';
import { useKitLookDraft } from './useKitLookDraft';
import { useThemeStillSong } from './useThemeStillSong';
import { themeMessages } from './themeMessages';
import { ThemePanelShell } from './ThemePanelShell';
import { ThemePanelBody } from './ThemePanelBody';
import { ThemeSummary } from './ThemeSummary';
import { ThemeDraftBar } from './ThemeDraftBar';

export interface DraftThemePanelProps {
  accountId: number;
  item: CatalogItem;
  kit: SerializedKit | null;
  assets: SerializedKitAsset[];
  hint?: string;
  onShowStale?: () => void;
  isCollapsed: boolean;
  onExpand: () => void;
  onCollapse: () => void;
  /** The page guards a leave while the draft is not saved. */
  onDirtyChange?: (isDirty: boolean) => void;
  /** The release page dialog: the dialog is the box, so the panel draws none. */
  isInDialog?: boolean;
}

/**
 * The Theme panel of the song page (design q2, q2b; owner request 2026-10-06). Closed, it shows the
 * saved look (`ThemeSummary`). Open, a theme, font, logo or colour pick changes only a draft: the panel
 * stays open and the preview shows the draft. While the draft differs from the saved look, Save
 * and Cancel take the place of "Done". Save sends the PATCH and closes the panel; Cancel brings
 * the saved look back and closes it. A failed save keeps the panel open with the draft.
 */
const DraftThemePanelComponent: React.FC<DraftThemePanelProps> = ({
  accountId,
  item,
  kit,
  assets,
  hint,
  onShowStale,
  isCollapsed,
  onExpand,
  onCollapse,
  onDirtyChange,
  isInDialog = false,
}) => {
  const intl = useIntl();
  const bodyId = useId();
  useKitWebFonts();
  const song = useThemeStillSong(item);
  const draft = useKitLookDraft({ accountId, catalogItemUuid: item.uuid, kit });
  const { change, save, discard, isDirty } = draft;

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  const pickTheme = useCallback((themeId: ReleaseThemeId) => change({ themeId }), [change]);
  const pickFont = useCallback((fontOverride: KitFontId | null) => change({ fontOverride }), [change]);
  const pickLogo = useCallback(
    (logoFileId: number | null, logoUrl: string | null) => change({ logoFileId }, logoUrl),
    [change]
  );
  const cancel = useCallback(() => {
    discard();
    onCollapse();
  }, [discard, onCollapse]);
  const saveAndClose = useCallback(() => {
    save()
      .then((isSaved) => {
        if (isSaved) {
          onCollapse();
        }
      })
      .catch(console.error);
  }, [onCollapse, save]);

  const headAction =
    !isCollapsed && !isDirty ? (
      <SMLButton onClick={onCollapse} ariaExpanded ariaControls={bodyId}>
        {intl.formatMessage(themeMessages.collapse)}
      </SMLButton>
    ) : undefined;

  return (
    <ThemePanelShell
      accountId={accountId}
      item={item}
      kit={kit}
      assets={assets}
      hint={hint}
      onShowStale={onShowStale}
      isInDialog={isInDialog}
      headAction={headAction}
    >
      {isCollapsed ? (
        <ThemeSummary
          themeId={draft.savedLook.themeId}
          fontOverride={draft.savedLook.fontOverride}
          palette={draft.savedPalette}
          song={song}
          bodyId={bodyId}
          onEdit={onExpand}
        />
      ) : (
        <ThemePanelBody
          accountId={accountId}
          bodyId={bodyId}
          artistName={item.artistName}
          look={draft.look}
          palette={draft.palette}
          editablePalette={draft.editablePalette}
          hasPaletteOverride={draft.hasPaletteOverride}
          onChangePalette={draft.changePalette}
          onResetPalette={draft.resetPalette}
          song={song}
          logoUrl={draft.logoUrl}
          saveAsDefault={draft.saveAsDefault}
          isSaving={draft.isSaving}
          onPickTheme={pickTheme}
          onPickFont={pickFont}
          onPickLogo={pickLogo}
          onToggleDefault={draft.toggleSaveAsDefault}
          footer={
            isDirty ? (
              <ThemeDraftBar
                isSaving={draft.isSaving}
                hasError={draft.hasError}
                onCancel={cancel}
                onSave={saveAndClose}
              />
            ) : undefined
          }
        />
      )}
    </ThemePanelShell>
  );
};

export const DraftThemePanel = TypedMemo(DraftThemePanelComponent);
